import clsx from "clsx";
import { useState } from "react";
import Markdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import type { AssistantStep, PendingQuestion } from "@/lib/types";

import { useAnswerQuestion, useCancelJob } from "@/hooks/useSession";
import { useStore } from "@/lib/store";

import { AssistantSteps } from "./AssistantSteps";

interface QuestionPromptProps {
  jobId: string;
  threadKey: string;
  questions: PendingQuestion[];
  partialResponse?: string | null;
  assistantSteps?: AssistantStep[];
}

/**
 * Renders an interactive question prompt from the AI agent.
 *
 * The AI asked one or more questions (each with optional preset options).
 * The user picks options or types a custom answer, then submits.
 * The response is sent to `POST /v1/jobs/:id/answer` which calls
 * `question.reply()` on the OpenCode session.
 */
export function QuestionPrompt({
  jobId,
  threadKey,
  questions,
  partialResponse,
  assistantSteps,
}: QuestionPromptProps): JSX.Element {
  const theme = useStore((state) => state.theme);

  // Each question gets its own answer state: either selected option labels or custom text.
  // answers[i] is a string[] — one entry per selected option, or a single custom string.
  const [answers, setAnswers] = useState<string[][]>(() => questions.map(() => []));
  const [customInputs, setCustomInputs] = useState<string[]>(() => questions.map(() => ""));

  const { mutateAsync, isPending } = useAnswerQuestion(threadKey);
  const { mutateAsync: cancelJobMutation, isPending: isCancelling } = useCancelJob(threadKey);
  const [error, setError] = useState<string | null>(null);
  const isBusy = isPending || isCancelling;

  function toggleOption(questionIndex: number, label: string): void {
    setAnswers((prev) => {
      const next = [...prev];
      const current = next[questionIndex] ?? [];
      const q = questions[questionIndex];

      if (q?.multiple) {
        // Toggle: add or remove
        if (current.includes(label)) {
          next[questionIndex] = current.filter((l) => l !== label);
        } else {
          next[questionIndex] = [...current, label];
        }
      } else {
        // Single select: replace
        next[questionIndex] = current.includes(label) ? [] : [label];
      }

      return next;
    });
  }

  function updateCustomInput(questionIndex: number, value: string): void {
    setCustomInputs((prev) => {
      const next = [...prev];
      next[questionIndex] = value;
      return next;
    });
  }

  function buildFinalAnswers(): string[][] {
    return questions.map((_, i) => {
      const selected = answers[i] ?? [];
      const custom = (customInputs[i] ?? "").trim();

      // If the user typed a custom answer and nothing is selected, use custom
      if (selected.length === 0 && custom) {
        return [custom];
      }
      // If they selected options and also typed custom, include both
      if (selected.length > 0 && custom) {
        return [...selected, custom];
      }
      return selected;
    });
  }

  const canSubmit = questions.every((_, i) => {
    const selected = answers[i] ?? [];
    const custom = (customInputs[i] ?? "").trim();
    return selected.length > 0 || custom.length > 0;
  });

  async function handleSubmit(): Promise<void> {
    if (!canSubmit || isBusy) return;
    setError(null);

    try {
      await mutateAsync({
        jobId,
        answers: buildFinalAnswers(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit answer");
    }
  }

  async function handleCancel(): Promise<void> {
    if (isBusy) return;
    setError(null);

    try {
      await cancelJobMutation(jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel");
    }
  }

  return (
    <div>
      {assistantSteps && assistantSteps.length > 0 && (
        <div className="mb-2">
          <AssistantSteps steps={assistantSteps} />
        </div>
      )}

      {/* Partial response the AI produced before asking */}
      {partialResponse && (
        <div
          className={clsx(
            "prose prose-sm mb-2 max-w-none rounded-lg border border-cyber-border bg-cyber-surface p-3 text-sm text-cyber-text prose-headings:my-3 prose-p:my-2 prose-a:text-cyber-cyan prose-code:rounded prose-code:bg-cyber-bg prose-code:px-1 prose-code:text-cyber-cyan prose-pre:my-2 prose-ol:my-2 prose-ul:my-2 prose-li:my-0",
            theme === "cyber" && "prose-invert",
          )}
        >
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
            {partialResponse}
          </Markdown>
        </div>
      )}

      <div className="rounded-lg border border-cyber-cyan/40 bg-cyber-cyan/5 p-4">
        <div className="mb-3 text-xs font-medium uppercase tracking-wider text-cyber-cyan">
          Input needed
        </div>

        {questions.map((q, qi) => (
          <div key={qi} className={qi > 0 ? "mt-4 border-t border-cyber-border pt-4" : ""}>
            {q.header && <div className="mb-1 text-sm font-medium text-cyber-text">{q.header}</div>}
            <div className="mb-2 text-sm text-cyber-text/90">{q.question}</div>

            {/* Option buttons */}
            {q.options.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {q.options.map((opt) => {
                  const isSelected = (answers[qi] ?? []).includes(opt.label);
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => toggleOption(qi, opt.label)}
                      disabled={isBusy}
                      className={`rounded-md border px-3 py-1.5 text-xs transition-all ${
                        isSelected
                          ? "border-cyber-cyan bg-cyber-cyan/20 text-cyber-cyan"
                          : "border-cyber-border bg-cyber-surface text-cyber-text hover:border-cyber-cyan/50 hover:text-cyber-cyan"
                      }`}
                      title={opt.description || undefined}
                    >
                      {opt.label}
                      {opt.description && (
                        <span className="ml-1 text-cyber-muted">- {opt.description}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Hint about multiple / custom */}
            {(q.multiple || q.custom !== false) && (
              <div className="mb-2 text-[11px] italic text-cyber-muted">
                {q.multiple && "You can select multiple. "}
                {q.custom !== false && "You can also type a custom answer."}
              </div>
            )}

            {/* Custom text input */}
            {q.custom !== false && (
              <input
                type="text"
                value={customInputs[qi] ?? ""}
                onChange={(e) => updateCustomInput(qi, e.target.value)}
                disabled={isBusy}
                placeholder="Type a custom answer..."
                className="w-full rounded border border-cyber-border bg-cyber-bg px-3 py-1.5 text-sm text-cyber-text placeholder-cyber-muted/50 focus:border-cyber-cyan/50 focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && canSubmit) {
                    e.preventDefault();
                    void handleSubmit();
                  }
                }}
              />
            )}
          </div>
        ))}

        {error && <div className="mt-2 text-xs text-cyber-red">{error}</div>}

        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || isBusy}
            className="rounded-md border border-cyber-cyan bg-cyber-cyan/10 px-4 py-1.5 text-xs font-medium text-cyber-cyan transition-all hover:bg-cyber-cyan/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPending ? "Submitting..." : "Submit Answer"}
          </button>
          <button
            type="button"
            onClick={() => void handleCancel()}
            disabled={isBusy}
            className="text-xs text-cyber-muted transition-all hover:text-cyber-red disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isCancelling ? "Cancelling..." : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}
